import React, { FormEventHandler, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { ListControlsRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { useControls } from 'src/components/async/model/control/useControls'
import { RequirementRenderer } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor'
import { RequirementDraft } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.types'
import { useSaveFrameworkSections } from 'src/pages/Frameworks/frameworks.hooks'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import type { RequirementEditSelection } from 'src/pages/Frameworks/show/frameworksShowSections.types'
import { ModalForm, ModalFormId } from '../..'

type Props = {
  framework: ComplianceFramework
  selectedRequirement: RequirementEditSelection | null
  clearSelectedRequirement: () => void
}

export const EditFrameworkRequirementModal = ({
  framework,
  selectedRequirement,
  clearSelectedRequirement,
}: Props): JSX.Element => {
  const show = useIsShowModal(ModalFormId.EDIT_FRAMEWORK_REQUIREMENT)
  const hideModal = useHideModal({
    modalId: ModalFormId.EDIT_FRAMEWORK_REQUIREMENT,
  })
  const saveSections = useSaveFrameworkSections()
  const cfModelId = framework.getModelId()
  const { data: controlsData, isLoading: controlsLoading } = useControls(
    new ListControlsRequest(),
    show,
  )
  const [requirementDraft, setRequirementDraft] =
    useState<RequirementDraft | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!show || !selectedRequirement) return
    setRequirementDraft(selectedRequirement.requirementDraft)
    setIsSubmitting(false)
  }, [selectedRequirement, show])

  const controlOptions = useMemo(
    () =>
      (controlsData?.getItemsList() ?? []).map((control) => ({
        name: `${control.getModelId()} ${control.getName()}`,
        value: control.getModelId(),
      })),
    [controlsData],
  )

  const closeAndReset = () => {
    hideModal()
    setRequirementDraft(null)
    setIsSubmitting(false)
    clearSelectedRequirement()
  }

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    if (!selectedRequirement || !requirementDraft) return
    setIsSubmitting(true)
    try {
      const newSections = selectedRequirement.sectionDraftsSnapshot.map((s) =>
        s.tempId !== selectedRequirement.sectionId
          ? s
          : {
              ...s,
              requirements: s.requirements.map((r) =>
                r.tempId === selectedRequirement.requirementTempId
                  ? requirementDraft
                  : r,
              ),
            },
      )
      await saveSections({
        complianceFrameworkModelId: cfModelId,
        originalSections: selectedRequirement.sectionDraftsSnapshot,
        newSections,
        controls: controlsData?.getItemsList() ?? [],
      })
      toast.success('Requirement updated.')
      closeAndReset()
    } catch (err) {
      const errorMessage = getErrorMessage(err as RpcError)
      toast.error(errorMessage.message)
      if (errorMessage.shouldLog) {
        log.error('EditFrameworkRequirementModal submit failed', err)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.EDIT_FRAMEWORK_REQUIREMENT}
      size="lg"
      title="Edit Requirement"
      submitText="Save"
      showOverflow
      disableSubmitButton={
        isSubmitting ||
        controlsLoading ||
        !selectedRequirement ||
        !requirementDraft
      }
    >
      <form id={ModalFormId.EDIT_FRAMEWORK_REQUIREMENT} onSubmit={onSubmit}>
        {requirementDraft && (
          <RequirementRenderer
            index={0}
            requirement={requirementDraft}
            controlOptions={controlOptions}
            onUpdate={(patch) =>
              setRequirementDraft((prev) =>
                prev ? { ...prev, ...patch } : prev,
              )
            }
            hideRowHeader
          />
        )}
      </form>
    </ModalForm>
  )
}
